import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import {
  useAnthology,
  useAddToAnthology,
  useAnthologyApprovedContent,
  useAnthologyContentTypeSummary,
  useUpdateAnthologyItems,
  useUpdateAnthologyCollectionOrder,
} from "../../anthologies/anthologyServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faFolderPlus,
  faBarsStaggered,
} from "@fortawesome/pro-regular-svg-icons";
import { useSelected } from "../OrganizeProvider";
import {
  createRemoveBody,
  getContentParentsForAnthology,
} from "../organizeUtil";
import { formatSort } from "../../../util/stringFormat";
import { mapDisplayOrder } from "../../common/dnd/dndUtils";
import {
  ascend,
  concat,
  descend,
  differenceWith,
  eqBy,
  equals,
  isEmpty,
  omit,
  map,
  mergeRight,
  nth,
  pick,
  pickBy,
  pluck,
  prop,
  sort,
  uniqBy,
} from "ramda";
import { notBlank } from "../../../util/ext";
import { useHistory, useLocation } from "react-router-dom";
import { useSearchQuery } from "../../common/use-searchQuery";
import {
  useAnthologyCollectionsCreator,
  useAnthologyCollectionsCount,
} from "../../creatorServices";
import { GroupBrowserContext } from "../OrganizeView";
import { parseIdQuery, parseTitleQuery } from "../../../util/fetchUtils";

import Grid from "@material-ui/core/Grid";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

import ContentGroupHeader from "./ContentGroupHeader";
import ContentGroupHeaderActions from "./ContentGroupHeaderActions";
import ContentGroupsBreadcrumbs from "./ContentGroupsBreadcrumbs";
import ContentGroupFilesBrowser from "./ContentGroupFilesBrowser";
import ContentGroupsPanel from "./ContentGroupsPanel";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import OrganizeButton from "../common/OrganizeButton";
import OrganizeContentGroupsBrowser from "./OrganizeContentGroupsBrowser";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";

const useStyles = makeStyles(theme => ({
  btn: {
    marginRight: 20,
  },

  endIcon: {
    color: theme.palette.primary.brandPrimary,
  },

  disabled: {
    color: "rgba(0, 0, 0, 0.26)",
  },
}));

const sortBy = {
  lastUpdated: {
    property: "auditUpdated",
    order: "desc",
  },
  asc: {
    property: "name",
    order: "asc",
  },
  desc: {
    property: "name",
    order: "desc",
  },
  displayOrder: {
    property: "collection.displayOrder",
    order: "asc",
  },
};

const getIds = pluck("id");
const deduplicate = uniqBy(prop("id"));
const byDate = (a, b) => new Date(b.auditUpdated) - new Date(a.auditUpdated);
const doSort = ({ order, property, list }) => {
  const byProp =
    order === "asc" ? ascend(prop(property)) : descend(prop(property));
  return sort(equals(property, "auditUpdated") ? byDate : byProp, list);
};

const reorderCollections = sortOption => collections =>
  mapDisplayOrder(
    doSort({
      property: sortOption.property,
      order: sortOption.order,
      list: collections,
    }),
  );

const initialContentState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: "ct.title",
  orderDirection: "asc",
  "ct.mediaId": "",
  "ct.title": "",
  source: {
    projectKey: "",
    projectname: "",
  },
};

const getInitialContentSearchState = initialState =>
  mergeRight(initialContentState, initialState);

const initialAnthologyCollectionsState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: sortBy.displayOrder.property,
  orderDirection: sortBy.displayOrder.order,
};

const OrganizeAnthologyBrowser = props => {
  const { breadcrumbs, id, view, SnackAlertHandler } = props;
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const { showAlert } = SnackAlertHandler;
  const { setExpanded } = useContext(GroupBrowserContext);
  const {
    selectedContent,
    removeAllSelectedContent,
    removeAllSelectedContentGroups,
  } = useSelected();

  //-- Manage Content Query
  const initialContentSearchState = getInitialContentSearchState(
    notBlank(location.state) ? location.state.searchState : {},
  );
  const contentSearchQuery = useSearchQuery(initialContentSearchState);
  const [contentSearchState, setContentSearchState] = useState({
    ...initialContentSearchState,
    source: contentSearchQuery.state.source,
    offset: contentSearchQuery.state.limit * contentSearchQuery.state.page,
    sort: formatSort(contentSearchQuery.state),
  });

  useEffect(() => {
    const { limit, page, appliedQuery } = contentSearchQuery.state;
    const defaultSort = formatSort({
      orderBy: initialContentSearchState.orderBy,
      orderDirection: initialContentSearchState.orderDirection,
    });

    const sort =
      contentSearchQuery.state.orderBy === null
        ? defaultSort
        : formatSort(contentSearchQuery.state);

    const queryState = omit(
      ["orderBy", "orderDirection", "page"],
      contentSearchQuery.state,
    );

    setContentSearchState({
      ...queryState,
      sort,
      offset: limit * page,
      source: contentSearchQuery.state.source,
      "ct.title": parseTitleQuery(appliedQuery),
      "ct.mediaId": parseIdQuery(appliedQuery),
    });
  }, [
    contentSearchQuery.state,
    initialContentSearchState.orderBy,
    initialContentSearchState.orderDirection,
  ]);

  //-- Manage Anthology Approved Content
  const contentResults = useAnthologyApprovedContent(id, {
    ...contentSearchState,
    source: contentSearchState.source.projectKey,
  });
  const contentTypeSummary = useAnthologyContentTypeSummary(id, {
    ...contentSearchState,
    source: contentSearchState.source.projectKey,
  });
  const [content, setContent] = useState([]);
  useEffect(() => {
    if (contentResults.isSuccess) {
      setContent(contentResults.data);
    }
  }, [contentResults.isSuccess, contentResults.data]);

  //-- Manage Anthology
  const anthologyResults = useAnthology(id);
  const addToAnthology = useAddToAnthology(id);
  const [anthology, setAnthology] = useState({});
  const [groupParent, setGroupParent] = useState({});
  useEffect(() => {
    if (anthologyResults.isSuccess) {
      setAnthology(anthologyResults.data);
      // Existence of parent module determines which header action render
      // TODO: Handle when breadcrumbs are absent and anthology has more than one parent module
      setGroupParent(
        nth(-2, breadcrumbs) || anthologyResults.data.parentModules[0],
      );
    }
  }, [anthologyResults.isSuccess, anthologyResults.data, breadcrumbs]);

  //-- Manage Anthology Collections
  const anthologyCollectionsQuery = useSearchQuery(
    initialAnthologyCollectionsState,
  );
  const [anthologyCollectionsState, setAnthologyCollectionsState] = useState({
    ...initialAnthologyCollectionsState,
    limit: initialAnthologyCollectionsState.limit,
    offset:
      anthologyCollectionsQuery.state.limit *
      anthologyCollectionsQuery.state.page,
    sort: `${anthologyCollectionsQuery.state.orderBy} ${anthologyCollectionsQuery.state.orderDirection}`,
  });
  useEffect(() => {
    const { limit, page, appliedQuery } = anthologyCollectionsQuery.state;

    const sort =
      anthologyCollectionsQuery.state.orderBy === null
        ? formatSort({
            orderBy: initialAnthologyCollectionsState.orderBy,
            orderDirection: initialAnthologyCollectionsState.orderDirection,
          })
        : formatSort(anthologyCollectionsQuery.state);

    const params = {
      q: appliedQuery,
      limit,
      sort,
      offset: limit !== "all" ? limit * page : null,
    };

    setAnthologyCollectionsState(pickBy(notBlank, params));
  }, [anthologyCollectionsQuery.state]);
  const anthologyCollections = useAnthologyCollectionsCreator(id, {
    ...anthologyCollectionsState,
  });
  const anthologyCollectionsCount = useAnthologyCollectionsCount(id, {
    q: anthologyCollectionsState.q,
  });
  const [collections, setCollections] = useState([]);
  const [initialCollections, setInitialCollections] = useState([]);
  useEffect(() => {
    if (anthologyCollections.isSuccess) {
      setCollections(prev => {
        const previous = anthologyCollectionsQuery.state.page === 0 ? [] : prev;
        const collections = concat(
          anthologyCollections.data.contents,
          previous,
        );

        return doSort({
          property: anthologyCollectionsQuery.state.orderBy,
          order: anthologyCollectionsQuery.state.orderDirection,
          list: deduplicate(collections),
        });
      });

      setInitialCollections(prev => {
        const previous = anthologyCollectionsQuery.state.page === 0 ? [] : prev;
        const collections = concat(
          anthologyCollections.data.contents,
          previous,
        );

        return doSort({
          property: anthologyCollectionsQuery.state.orderBy,
          order: anthologyCollectionsQuery.state.orderDirection,
          list: deduplicate(collections),
        });
      });
    }
  }, [
    anthologyCollections.data,
    anthologyCollections.isSuccess,
    anthologyCollectionsQuery.state,
  ]);

  //-- Manage Filter Menu State
  const [sortOption, setSortOption] = useState(sortBy.displayOrder);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = event => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const onSortClick = sortOption => event => {
    setSortOption(sortOption);
    setAnchorEl(null);

    setCollections(reorderCollections(sortOption));
  };

  const handleEditClick = () => {
    const { pathname, search } = location;
    const fromPath = notBlank(search) ? pathname + search : pathname;
    history.push(`/creator/anthologies/${id}`, {
      from: fromPath,
    });
  };

  //-- Handle Removing Selected Content from Anthology
  const removeContentFromCollection = useRemoveContentFromCollection();
  const handleDeleteItems = async () => {
    if (notBlank(anthology)) {
      try {
        const collections = getContentParentsForAnthology(
          selectedContent,
          anthology,
        );

        await Promise.all(
          map(
            collectionId =>
              removeContentFromCollection.mutateAsync({
                collectionId: collectionId,
                contentIds: getIds(selectedContent),
              }),
            getIds(collections),
          ),
        );

        showAlert("success", ["Content removed from Anthology"]);
        removeAllSelectedContent();
      } catch (err) {
        showAlert("error", [err.message]);
      }
    }
  };

  //-- Handle Removing Collection from Anthology
  const updateAnthologyItems = useUpdateAnthologyItems();
  const removeCollection = useCallback(
    async collection => {
      try {
        const parent = {
          id,
          mediaType: "anthology",
          prevMediaList: collections,
        };

        const body = createRemoveBody({
          parent,
          newChildrenIds: [collection.id],
        });

        await updateAnthologyItems.mutateAsync({
          resourceIds: { id: body.id },
          data: { ids: body.ids },
        });

        showAlert("success", [`Content Group Successfully Removed`]);
      } catch (error) {
        console.log(error);
        showAlert("error", [error.message]);
      }
    },
    [collections, id, showAlert, updateAnthologyItems],
  );

  const resetQuery = () => {
    setSortOption(sortBy.displayOrder);
    setAnthologyCollectionsState({
      limit: initialAnthologyCollectionsState.limit,
      offset:
        initialAnthologyCollectionsState.limit *
        initialAnthologyCollectionsState.page,
      sort: `${anthologyCollectionsQuery.state.orderBy} ${anthologyCollectionsQuery.state.orderDirection}`,
    });
  };

  //-- Manage Collection Order
  const [reorderingCollections, setReorderingCollections] = useState(false);
  const handleClickReorder = () => {
    removeAllSelectedContentGroups();
    setExpanded(prev => ({
      ...prev,
      contentGroups: { outer: true, inner: true },
    }));
    setReorderingCollections(true);
    anthologyCollectionsQuery.handleUpdateSort({
      property: initialAnthologyCollectionsState.orderBy,
      order: initialAnthologyCollectionsState.orderDirection,
    });
    anthologyCollectionsQuery.handleUpdateLimit("all");
  };

  const updateAnthologyCollectionOrder = useUpdateAnthologyCollectionOrder();
  const handleSaveReorder = async () => {
    const updated = differenceWith(
      (updated, previous) =>
        eqBy(prop("id"), updated, previous) &&
        eqBy(prop("displayOrder"), updated, previous),
      collections,
      initialCollections,
    );

    try {
      // await updateAnthologyCollectionOrder.mutateAsync({
      //   anthologyId: id,
      //   update: map(pick(["id", "displayOrder"]), updated),
      // });
      await updateAnthologyCollectionOrder.mutateAsync({
        resourceIds: { id: id },
        data: map(pick(["id", "displayOrder"]), updated),
      });
      resetQuery();
      setExpanded(prev => ({
        ...prev,
        contentGroups: { outer: true, inner: false },
      }));
      setReorderingCollections(false);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };
  const handleCancelReorder = () => {
    resetQuery();
    setExpanded(prev => ({
      ...prev,
      contentGroups: { outer: true, inner: false },
    }));
    setReorderingCollections(false);
  };

  //-- Loading State
  const [contentLoading, setContentLoading] = useState(false);
  useEffect(() => {
    setContentLoading(
      contentResults.isLoading || removeContentFromCollection.isLoading,
    );
  }, [contentResults.isLoading, removeContentFromCollection.isLoading]);

  return (
    <OrganizeContentGroupsBrowser
      view={view}
      header={
        <ContentGroupHeader
          loading={anthologyResults.isLoading}
          view={view}
          breadcrumbs={
            <ContentGroupsBreadcrumbs
              media={anthologyResults}
              breadcrumbs={breadcrumbs}
            />
          }
          actions={
            <ContentGroupHeaderActions
              group={anthology}
              groupParent={groupParent}
              handleEditClick={handleEditClick}
              action="move"
              moveActionTitle="Move"
              moveActionIcon={faFolderPlus}
            />
          }
        />
      }
      contentGroupsPanel={
        <ContentGroupsPanel
          reordering={reorderingCollections}
          hasMore={
            anthologyCollections.isSuccess && anthologyCollections.data.hasMore
          }
          view={view}
          loading={
            anthologyCollections.isLoading ||
            updateAnthologyCollectionOrder.isLoading
          }
          showAlert={showAlert}
          parent={anthology}
          addToParent={addToAnthology}
          removeFromParent={removeCollection}
          totalGroups={
            anthologyCollectionsCount.isSuccess
              ? anthologyCollectionsCount.data.count
              : "-"
          }
          setContentGroups={setCollections}
          contentGroups={collections}
          types={["collection"]}
          handleContentGroupsPageChange={() =>
            anthologyCollectionsQuery.handleUpdatePage(
              anthologyCollectionsQuery.state.page + 1,
            )
          }
          searchBar={
            !reorderingCollections ? (
              <RealTimeSearchBar
                searchBarEnd
                query={anthologyCollectionsQuery.state.appliedQuery}
                onQueryChange={anthologyCollectionsQuery.handleUpdateQuery}
              />
            ) : null
          }
          filterMenu={
            reorderingCollections ? (
              <Fragment>
                <Grid item xs="auto">
                  <OrganizeButton
                    style={{ marginRight: 10 }}
                    variant="outlined"
                    color="primary"
                    onClick={handleClick}
                    // onKeyDown={handleKeyDown(filterButtons.sortBy)}
                    endIcon={<FontAwesomeIcon icon={faAngleDown} />}
                    classes={{
                      endIcon: classes.endIcon,
                    }}
                  >
                    Order By
                  </OrganizeButton>
                  <Menu
                    id="sort-anthology-collections-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleSortClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    getContentAnchorEl={null}
                  >
                    <MenuItem
                      selected={sortOption === sortBy.lastUpdated}
                      onClick={onSortClick(sortBy.lastUpdated)}
                    >
                      <ListItemText>Last Updated</ListItemText>
                    </MenuItem>

                    <MenuItem
                      selected={sortOption === sortBy.asc}
                      onClick={onSortClick(sortBy.asc)}
                    >
                      <ListItemText>Ascending (A - Z)</ListItemText>
                    </MenuItem>

                    <MenuItem
                      selected={sortOption === sortBy.desc}
                      onClick={onSortClick(sortBy.desc)}
                    >
                      <ListItemText>Descending (Z - A)</ListItemText>
                    </MenuItem>
                  </Menu>
                </Grid>

                <Grid item xs="auto">
                  <OrganizeButton
                    variant="contained"
                    color="primary"
                    style={{ height: 36 }}
                    onClick={handleSaveReorder}
                  >
                    Save
                  </OrganizeButton>
                </Grid>

                <Grid item xs="auto">
                  <OrganizeButton color="primary" onClick={handleCancelReorder}>
                    Cancel
                  </OrganizeButton>
                </Grid>
              </Fragment>
            ) : !isEmpty(collections) ? (
              <OrganizeButton
                color="primary"
                variant="outlined"
                onClick={handleClickReorder}
                endIcon={<FontAwesomeIcon icon={faBarsStaggered} />}
                classes={{ endIcon: classes.endIcon }}
                style={{ marginRight: 15 }}
              >
                Reorder
              </OrganizeButton>
            ) : null
          }
        />
      }
      filesPanel={
        <ContentGroupFilesBrowser
          searchState={contentSearchState}
          sourceFilter={contentSearchQuery.state.source}
          searchBar={
            <RealTimeSearchBar
              searchBarEnd
              query={contentSearchQuery.state.appliedQuery}
              onQueryChange={query =>
                contentSearchQuery.handleUpdateQuery(query)
              }
            />
          }
          filterMenu={
            <ContentSourcesAutocomplete
              setSource={contentSearchQuery.handleUpdateSource}
              source={contentSearchQuery.state.source}
            />
          }
          loading={contentLoading}
          setLoading={setContentLoading}
          content={content}
          total={
            contentTypeSummary.isLoading ? "-" : contentTypeSummary.data._ALL
          }
          parent={anthology}
          page={contentSearchQuery.state.page}
          orderBy={contentSearchQuery.state.orderBy}
          order={contentSearchQuery.state.orderDirection}
          selectedContent={selectedContent}
          handlePageChange={(event, newPage) => {
            contentSearchQuery.handleUpdatePage(newPage);
          }}
          handleSortChange={contentSearchQuery.handleUpdateSort}
          rowsPerPage={contentSearchQuery.state.limit}
          onRowsPerPageChange={contentSearchQuery.handleUpdateLimit}
          handleDeleteItems={handleDeleteItems}
          SnackAlertHandler={SnackAlertHandler}
        />
      }
    />
  );
};

export default OrganizeAnthologyBrowser;
